import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../../config';
import { RootState } from '../store';
import { handleApiError } from '../../utils/apiErrorHandler';

// Define interfaces for our API data
export interface WhatsappPhone {
  id: number;
  phone_number: string;
  phone_number_id?: string;
  display_name: string;
  business_account_id?: string;
  access_token?: string;
  verify_token?: string;
  status: string;
  connected: boolean;
  created_at: string;
  updated_at: string;
  description?: string;
  webhook_url?: string;
  is_default?: boolean;
  is_active?: boolean;
}

// Lightweight user interface for current user
export interface CurrentUser {
  id: number;
  email: string;
  name: string;
  role?: string;
  phone_number?: string;
  is_active?: boolean;
  is_superuser?: boolean;
}

export interface Contact {
  id: number;
  name: string;
  phone_number: string;
  email?: string;
  tags?: string[];
  opt_in_status: boolean;
  opt_in_timestamp?: string;
  
  // Marketing opt-out fields
  marketing_opt_out?: boolean;
  marketing_opt_out_timestamp?: string;
  marketing_opt_out_reason?: string;
  
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface ContactGroup {
  id: number;
  name: string;
  description?: string;
  created_by: number;
  created_at: string;
  updated_at?: string;
  memberCount?: number;
}

export interface ContactGroupWithMembers extends ContactGroup {
  members: Contact[];
}

export interface MessageReaction {
  message_id: string;
  emoji: string;
  from?: string;
  timestamp?: string;
}

export interface MediaMetadata {
  media_id?: string;
  storage_key?: string;
  media_asset_id?: number;
}

export interface Message {
  id: number;
  whatsapp_phone_id: number;
  contact_id: number;
  contact: {
    id: number;
    name: string;
    phone_number: string;
  };
  direction: 'inbound' | 'outbound';
  message_type: 'text' | 'media' | 'template' | 'image' | 'video' | 'audio' | 'document';
  content: string;
  media_url?: string;
  status: 'sent' | 'delivered' | 'read' | 'failed';
  timestamp: string;
  created_at: string;
  updated_at: string;
  reactions?: MessageReaction;
  media_metadata?: MediaMetadata;
  error_code?: string;
  error_message?: string;
  error_details?: any;
}

export interface Template {
  id: number;
  name: string;
  content: string;
  variables: string[];
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED';
  language_code: string;
  category: string;
  parameter_format: 'POSITIONAL' | 'NAMED';
  allow_category_change: boolean;
  whatsapp_template_id?: string;
  whatsapp_business_account_id?: string;
  rejected_reason?: string;
  is_submitted: boolean;
  components?: any[];
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  approved_at?: string;
}

// Interface already defined above - removed duplicate definition

export interface Campaign {
  id: number;
  name: string;
  template_id: number;
  template: Template;
  status: 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  stats?: {
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    total: number;
  };
}

export interface DashboardStats {
  total_contacts: number;
  total_messages: number;
  active_campaigns: number;
  message_stats: {
    sent: number;
    delivered: number;
    read: number;
    failed: number;
  };
  recent_messages: Message[];
}

// Create our API with endpoints
export const whatsappApi = createApi({
  reducerPath: 'whatsappApi',
  baseQuery: async (args, api, extraOptions) => {
    // Use fetchBaseQuery as the underlying query function
    const result = await fetchBaseQuery({
      baseUrl: API_BASE_URL,
      prepareHeaders: (headers, { getState }) => {
        // Get token from auth state
        const token = (getState() as RootState).auth.token;
        
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
        
        return headers;
      },
    })(args, api, extraOptions);
    
    // Handle errors like 403 Forbidden
    if (result.error) {
      handleApiError(result.error, typeof args === 'string' ? args : args.url);
    }
    
    return result;
  },
  tagTypes: [
    'WhatsAppPhones',
    'Contacts',
    'ContactGroups',
    'Users',
    'Messages',
    'Templates',
    'Campaigns',
    'Stats',
    'MediaAssets',
  ],
  endpoints: (builder) => ({
    // Current user
    getCurrentUser: builder.query<CurrentUser, void>({
      query: () => '/users/me',
      providesTags: ['Users']
    }),
    // WhatsApp Phones
    getWhatsAppPhones: builder.query<WhatsappPhone[], void>({
      query: () => '/whatsapp/phones/',
      providesTags: ['WhatsAppPhones']
    }),
    
    getWhatsAppPhone: builder.query<WhatsappPhone, number>({
      query: (id) => `/whatsapp/phones/${id}`,
      providesTags: (result, error, id) => [{ type: 'WhatsAppPhones', id }]
    }),
    
    addWhatsAppPhone: builder.mutation<WhatsappPhone, Partial<WhatsappPhone>>({
      query: (body) => ({
        url: '/whatsapp/phones/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WhatsAppPhones']
    }),
    
    updateWhatsappPhone: builder.mutation<WhatsappPhone, Partial<WhatsappPhone>>({
      query: (body) => ({
        url: `/whatsapp/phones/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, body) => [{ type: 'WhatsAppPhones', id: body.id }]
    }),
    
    deleteWhatsappPhone: builder.mutation<void, number>({
      query: (id) => ({
        url: `/whatsapp/phones/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WhatsAppPhones']
    }),
    
    connectWhatsappPhone: builder.mutation<{qr_code: string}, number>({
      query: (id) => ({
        url: `/whatsapp/phones/${id}/connect`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'WhatsAppPhones', id }]
    }),
    
    disconnectWhatsappPhone: builder.mutation<void, number>({
      query: (id) => ({
        url: `/whatsapp/phones/${id}/disconnect`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'WhatsAppPhones', id }]
    }),
    
    refreshQRCode: builder.mutation<{qr_code: string}, number>({
      query: (id) => ({
        url: `/whatsapp/phones/${id}/refresh-qr`,
        method: 'POST',
      }),
    }),
    
    verifyPhoneWithPin: builder.mutation<void, {id: number, pin: string}>({
      query: ({id, pin}) => ({
        url: `/whatsapp/phones/${id}/verify-pin`,
        method: 'POST',
        body: { pin },
      }),
      invalidatesTags: (result, error, {id}) => [{ type: 'WhatsAppPhones', id }]
    }),
    
    // Contacts
    getContacts: builder.query<{ contacts: Contact[]; total: number; skip: number; limit: number }, { limit?: number; skip?: number }>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
          queryParams.append('limit', String(params.limit));
        }
        if (params.skip !== undefined) {
          queryParams.append('skip', String(params.skip));
        }
        const queryString = queryParams.toString();
        return `/contacts/${queryString ? '?' + queryString : ''}`;
      },
      providesTags: ['Contacts']
    }),
    
    searchContacts: builder.query<{ contacts: Contact[]; total: number; skip: number; limit: number }, { query: string; field?: string; sort_by?: string; sort_order?: string; opt_in_status?: boolean; tags?: string; created_after?: string; created_before?: string; skip?: number; limit?: number }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        queryParams.append('query', params.query);
        if (params.field && params.field !== 'all') {
          queryParams.append('field', params.field);
        }
        if (params.sort_by) {
          queryParams.append('sort_by', params.sort_by);
        }
        if (params.sort_order) {
          queryParams.append('sort_order', params.sort_order);
        }
        if (params.opt_in_status !== undefined) {
          queryParams.append('opt_in_status', String(params.opt_in_status));
        }
        if (params.tags) {
          queryParams.append('tags', params.tags);
        }
        if (params.created_after) {
          queryParams.append('created_after', params.created_after);
        }
        if (params.created_before) {
          queryParams.append('created_before', params.created_before);
        }
        if (params.skip !== undefined) {
          queryParams.append('skip', String(params.skip));
        }
        if (params.limit !== undefined) {
          queryParams.append('limit', String(params.limit));
        }
        return `/contacts/search?${queryParams.toString()}`;
      },
      providesTags: ['Contacts']
    }),
    
    // Contact Groups
    getContactGroups: builder.query<ContactGroup[], void>({
      query: () => '/contact-groups/',
      providesTags: ['ContactGroups', 'Contacts']
    }),
    
    getContactGroup: builder.query<ContactGroupWithMembers, number>({
      query: (id) => `/contact-groups/${id}`,
      providesTags: (result, error, id) => [{ type: 'ContactGroups', id }]
    }),
    
    createContactGroup: builder.mutation<ContactGroup, Partial<ContactGroup>>({
      query: (body) => ({
        url: '/contact-groups/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ContactGroups']
    }),
    
    updateContactGroup: builder.mutation<ContactGroup, { id: number; body: Partial<ContactGroup> }>({
      query: ({ id, body }) => ({
        url: `/contact-groups/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'ContactGroups', id }]
    }),
    
    deleteContactGroup: builder.mutation<void, number>({
      query: (id) => ({
        url: `/contact-groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ContactGroups', 'Contacts']
    }),
    
    addGroupMembers: builder.mutation<ContactGroup, { groupId: number; contactIds: number[] }>({
      query: ({ groupId, contactIds }) => ({
        url: `/contact-groups/${groupId}/members`,
        method: 'POST',
        body: contactIds
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'ContactGroups', id: groupId }]
    }),
    
    removeGroupMembers: builder.mutation<ContactGroup, { groupId: number; contactIds: number[] }>({
      query: ({ groupId, contactIds }) => ({
        url: `/contact-groups/${groupId}/members`,
        method: 'DELETE',
        body: contactIds
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: 'ContactGroups', id: groupId }]
    }),
    
    getContact: builder.query<Contact, number>({
      query: (id) => `/contacts/${id}`,
      providesTags: (result, error, id) => [{ type: 'Contacts', id }]
    }),
    
    createContact: builder.mutation<Contact, Partial<Contact>>({
      query: (body) => ({
        url: '/contacts/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Contacts']
    }),
    
    updateContact: builder.mutation<Contact, { id: number; body: Partial<Contact> }>({
      query: ({ id, body }) => ({
        url: `/contacts/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Contacts', id }]
    }),
    
    deleteContact: builder.mutation<void, number>({
      query: (id) => ({
        url: `/contacts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Contacts']
    }),
    
    importContactsCSV: builder.mutation<{success: boolean, count: number, skipped: number, errors?: string[], invalid_contacts?: Array<{name: string, raw_phone: string, reason: string}>}, FormData>({
      query: (formData) => ({
        url: '/contacts/import/csv',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Contacts']
    }),
    
    getCSVTemplate: builder.query<{filename: string, content: string}, void>({
      query: () => ({
        url: '/contacts/import/template',
        method: 'GET',
      }),
    }),
    
    bulkDeleteContacts: builder.mutation<{success: boolean, count: number}, number[]>({
      query: (ids) => ({
        url: '/contacts/bulk-delete',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['Contacts']
    }),
    
    // Messages
    getMessages: builder.query<{ messages: Message[]; total: number; skip: number; limit: number }, { limit?: number; skip?: number }>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit !== undefined) {
          queryParams.append('limit', String(params.limit));
        }
        if (params.skip !== undefined) {
          queryParams.append('skip', String(params.skip));
        }
        const queryString = queryParams.toString();
        return `/messages/${queryString ? '?' + queryString : ''}`;
      },
      providesTags: ['Messages'],
      // Force refresh contacts when messages are fetched, as they might contain opt-out commands
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate contacts cache to force a refresh
          dispatch(whatsappApi.util.invalidateTags(['Contacts']));
        } catch {}
      }
    }),
    
    getMessagesByContact: builder.query<Message[], number>({
      query: (contactId) => `/messages/contact/${contactId}`,
      providesTags: ['Messages'],
      // Force refresh contacts when messages are fetched, as they might contain opt-out commands
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate contacts cache to force a refresh
          dispatch(whatsappApi.util.invalidateTags(['Contacts']));
        } catch {}
      }
    }),
    
    sendMessage: builder.mutation<Message, { whatsapp_phone_id: number; contact_id: number; content: string; message_type: string; recipient_phone: string; media_url?: string; template_id?: number; template_params?: any }>({
      query: (body) => ({
        url: '/messages/send',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Messages']
    }),
    
    sendMediaMessage: builder.mutation<Message, FormData>({
      query: (formData) => ({
        url: '/messages/media',
        method: 'POST',
        body: formData,
        // Don't set Content-Type header, the browser will set it with boundary for FormData
        formData: true,
      }),
      invalidatesTags: ['Messages']
    }),
    
    deleteMessage: builder.mutation<Message, { messageId: number, reason?: string }>({
      query: ({ messageId, reason }) => ({
        url: `/messages/${messageId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Messages']
    }),
    
    // Templates
    getTemplates: builder.query<Template[], { status?: string }>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) {
          queryParams.append('status', params.status);
        }
        const queryString = queryParams.toString();
        return `/templates/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Templates']
    }),
    
    getTemplate: builder.query<Template, number>({
      query: (id) => `/templates/${id}`,
      providesTags: (result, error, id) => [{ type: 'Templates', id }]
    }),
    
    createTemplate: builder.mutation<Template, Partial<Template>>({
      query: (body) => ({
        url: '/templates/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Templates']
    }),
    
    updateTemplate: builder.mutation<Template, Partial<Template>>({
      query: (body) => ({
        url: `/templates/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, body) => [{ type: 'Templates', id: body.id }]
    }),
    
    deleteTemplate: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Templates']
    }),
    
    submitTemplate: builder.mutation<{status: string, message: string, template_id: number, whatsapp_template_id: string, name: string, whatsapp_status: string}, number>({
      query: (id) => ({
        url: `/templates/${id}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Templates', id }]
    }),
    
    checkTemplateStatus: builder.query<{status: string, template_id: number, whatsapp_template_id: string, name: string, whatsapp_status: string, language_code?: string, rejected_reason?: string, submitted_at?: string, approved_at?: string}, number>({
      query: (id) => `/templates/${id}/status`,
      providesTags: (result, error, id) => [{ type: 'Templates', id }]
    }),
    
    // Campaigns
    getCampaigns: builder.query<
      Campaign[],
      { orderBy?: string; orderDir?: 'asc' | 'desc'; whatsapp_phone_id?: number; limit?: number; skip?: number } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        const p: any = params || {};
        if (p.orderBy) queryParams.append('order_by', p.orderBy);
        if (p.orderDir) queryParams.append('order_dir', p.orderDir);
        if (p.whatsapp_phone_id) queryParams.append('whatsapp_phone_id', String(p.whatsapp_phone_id));
        if (p.limit) queryParams.append('limit', String(p.limit));
        if (p.skip) queryParams.append('skip', String(p.skip));
        const qs = queryParams.toString();
        return `/campaigns/${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Campaigns']
    }),
    
    getCampaign: builder.query<Campaign, number>({
      query: (id) => `/campaigns/${id}`,
      providesTags: (result, error, id) => [{ type: 'Campaigns', id }]
    }),
    
    createCampaign: builder.mutation<Campaign, Partial<Campaign>>({
      query: (body) => ({
        url: '/campaigns/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Campaigns']
    }),

    updateCampaign: builder.mutation<Campaign, Partial<Campaign>>({
      query: (body) => ({
        url: `/campaigns/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, body) => [{ type: 'Campaigns', id: body.id }]
    }),

    deleteCampaign: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/campaigns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaigns']
    }),
    
    // Campaign action endpoints
    scheduleCampaign: builder.mutation<{status: string, message: string}, number>({
      query: (id) => ({
        url: `/campaigns/actions/${id}/schedule`,
        method: 'POST',
      }),
      invalidatesTags: ['Campaigns']
    }),
    
    pauseCampaign: builder.mutation<{status: string, message: string}, number>({
      query: (id) => ({
        url: `/campaigns/actions/${id}/pause`,
        method: 'POST',
      }),
      invalidatesTags: ['Campaigns']
    }),
    
    resumeCampaign: builder.mutation<{status: string, message: string}, number>({
      query: (id) => ({
        url: `/campaigns/actions/${id}/resume`,
        method: 'POST',
      }),
      invalidatesTags: ['Campaigns']
    }),

    // Media Assets endpoints
    getMediaAssets: builder.query<any[], void>({
      query: () => '/media-assets',
      providesTags: ['MediaAssets']
    }),
    
    getMediaAsset: builder.query<any, number>({
      query: (id) => `/media-assets/${id}`,
      providesTags: (result, error, id) => [{ type: 'MediaAssets', id }]
    }),
    
    getMediaAssetUrl: builder.query<any, { id: number, phoneNumberId?: string }>({
      query: ({ id, phoneNumberId }) => {
        const url = `/media-assets/${id}/url`;
        return phoneNumberId ? `${url}?phone_number_id=${phoneNumberId}` : url;
      },
      providesTags: (result, error, arg) => [{ type: 'MediaAssets', id: arg.id }]
    }),
    
    getWhatsAppMediaUrl: builder.query<any, { mediaId: string, phoneNumberId?: string }>({
      query: ({ mediaId, phoneNumberId }) => {
        const url = `/media-assets/whatsapp/${mediaId}/url`;
        return phoneNumberId ? `${url}?phone_number_id=${phoneNumberId}` : url;
      },
      providesTags: ['MediaAssets']
    }),
    
    getMediaProxyUrl: builder.query<string, { mediaId: string, phoneNumberId?: string }>({
      query: () => '',  // This is a dummy query that won't be executed
      transformResponse: (response, meta, arg) => {
        // Return the URL to the media proxy endpoint
        const { mediaId, phoneNumberId } = arg;
        const baseUrl = `${API_BASE_URL}/media-proxy/${mediaId}`;
        return phoneNumberId ? `${baseUrl}?phone_number_id=${phoneNumberId}` : baseUrl;
      },
      providesTags: ['MediaAssets']
    }),
    
    // R2 Secure Media endpoints
    getSecureMediaUrl: builder.query<{ url: string, expires_in: number, media_type: string, file_name: string }, number>({
      query: (mediaId) => `/media/secure/${mediaId}`,
      providesTags: (result, error, id) => [{ type: 'MediaAssets', id }]
    }),
    
    getSecureMediaProxyUrl: builder.query<string, number>({
      query: () => '',  // This is a dummy query that won't be executed
      transformResponse: (response, meta, arg) => {
        // Return the URL to the secure media proxy endpoint
        return `${API_BASE_URL}/media/proxy/${arg}`;
      },
      providesTags: ['MediaAssets']
    }),
    
    uploadMediaAsset: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/media-assets',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['MediaAssets']
    }),
    
    deleteMediaAsset: builder.mutation<void, number>({
      query: (id) => ({
        url: `/media-assets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MediaAssets']
    }),
    
    // Dashboard stats
    getDashboardStats: builder.query<DashboardStats, void>({
      query: () => '/stats/dashboard',
      providesTags: ['Stats']
    }),

    // Analytics time-series
    getTimeSeriesStats: builder.query<
      {[date: string]: {sent: number, delivered: number, read: number, failed: number}},
      {days?: number} | void
    >({
      query: (params) => {
        const p: any = params || {};
        const queryParams = new URLSearchParams();
        if (p.days) queryParams.append('days', String(p.days));
        const qs = queryParams.toString();
        return `/stats/analytics/time-series${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['Stats']
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetCurrentUserQuery,
  useGetWhatsAppPhonesQuery: useGetWhatsappPhonesQuery,
  useSendMessageMutation,
  useSendMediaMessageMutation,
  useAddWhatsAppPhoneMutation: useAddWhatsappPhoneMutation,
  useUpdateWhatsappPhoneMutation,
  useDeleteWhatsappPhoneMutation,
  useConnectWhatsappPhoneMutation,
  useGetContactsQuery,
  useSearchContactsQuery,
  useGetContactQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useGetMessagesByContactQuery,
  useGetContactGroupsQuery,
  useGetContactGroupQuery,
  useCreateContactGroupMutation,
  useUpdateContactGroupMutation,
  useDeleteContactGroupMutation,
  useAddGroupMembersMutation,
  useRemoveGroupMembersMutation,
  useGetMessagesQuery,
  useDeleteMessageMutation,
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useSubmitTemplateMutation,
  useCheckTemplateStatusQuery,
  useGetCampaignsQuery,
  useGetCampaignQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useGetDashboardStatsQuery,
  useGetTimeSeriesStatsQuery,
  useScheduleCampaignMutation,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useGetMediaAssetsQuery,
  useGetMediaAssetQuery,
  useGetMediaAssetUrlQuery,
  useGetWhatsAppMediaUrlQuery,
  useGetMediaProxyUrlQuery,
  useGetSecureMediaUrlQuery,
  useGetSecureMediaProxyUrlQuery,
  useUploadMediaAssetMutation,
  useDeleteMediaAssetMutation,
  useImportContactsCSVMutation,
} = whatsappApi;
